import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Home.css';

const AvatarCreator: React.FC = () => {
  const { user, token, login } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // Avatar State
  const [skinTone, setSkinTone] = useState('#FFDBAC');
  const [hairStyle, setHairStyle] = useState('short');
  const [hairColor, setHairColor] = useState('#4B2C20');
  const [eyeStyle, setEyeStyle] = useState('dot');
  const [mouthStyle, setMouthStyle] = useState('smile');
  const [clothing, setClothing] = useState('shirt');
  const [clothingColor, setClothingColor] = useState('#3498db');
  const [background, setBackground] = useState('#f0f4f8');
  const [highlightColor, setHighlightColor] = useState('transparent');

  // Options
  const skinTones = ['#FFDBAC', '#F1C27D', '#E0AC69', '#8D5524', '#C68642'];
  const hairStyles = ['none', 'short', 'long', 'bob', 'spiky', 'ponytail', 'long ponytail', 'curly male', 'curly female'];
  const hairColors = [
    '#000000', '#4B2C20', '#6F4E37', '#A67B5B', '#A52A2A', '#D66D0D', 
    '#D4AF37', '#F5E1A4', '#FFFFFF', '#C0C0C0', '#FF69B4', '#E74C3C', 
    '#2ECC71', '#1ABC9C', '#3498DB', '#9B59B6'
  ];
  const highlightColors = [
    'transparent', '#FFFFFF', '#F5E1A4', '#D4AF37', '#FF69B4', '#E74C3C', 
    '#3498DB', '#2ECC71', '#9B59B6', '#FFD700', '#A67B5B'
  ];
  const eyeStyles = ['dot', 'happy', 'blink', 'cool'];
  const mouthStyles = ['smile', 'flat', 'surprised'];
  const clothingTypes = ['shirt', 'hoodie', 'dress'];
  const clothingColors = ['#3498db', '#e74c3c', '#2ecc71', '#f1c40f', '#9b59b6', '#34495e'];
  const backgroundColors = ['#f0f4f8', '#e3f2fd', '#fce4ec', '#e8f5e9', '#fff3e0', '#f3e5f5'];

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const renderHighlightPaths = (style: string) => {
    switch (style) {
      case 'short':
      case 'spiky':
        return `
          <path d="M35 25 Q50 20 65 25" />
          <path d="M30 35 Q50 32 70 35" />
        `;
      case 'curly male':
        return `
          <path d="M30 20 Q35 15 40 20" />
          <path d="M60 20 Q65 15 70 20" />
          <path d="M42 12 Q50 8 58 12" />
          <path d="M25 30 Q30 25 35 30" />
          <path d="M65 30 Q70 25 75 30" />
        `;
      case 'curly female':
        return `
          <path d="M30 25 Q50 20 70 25" />
          <path d="M18 55 Q18 70 18 80" />
          <path d="M82 55 Q82 70 82 80" />
          <path d="M35 35 Q40 32 45 35" />
          <path d="M55 35 Q60 32 65 35" />
          <path d="M22 65 Q28 62 34 65" />
          <path d="M66 65 Q72 62 78 65" />
        `;
      case 'long':
      case 'bob':
        return `
          <path d="M30 30 Q50 25 70 30" />
          <path d="M22 55 L22 80" />
          <path d="M78 55 L78 80" />
          <path d="M28 60 L28 85" />
          <path d="M72 60 L72 85" />
        `;
      case 'ponytail':
      case 'long ponytail':
        return `
          <path d="M35 25 Q50 20 65 25" />
          <path d="M78 45 Q85 35 90 50" />
          <path d="M82 55 Q85 65 82 80" />
        `;
      default:
        return '';
    }
  };

  const generateSVG = (isDataUrl = true) => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <!-- Background -->
        <circle cx="50" cy="50" r="50" fill="${background}" />
        
        <!-- Hair Back (Behind head) -->
        ${hairStyle === 'long' ? `
          <path d="M20 45 Q20 10 50 10 Q80 10 80 45 L85 90 Q85 100 70 100 L30 100 Q15 100 15 90 Z" fill="${hairColor}" />
        ` : hairStyle === 'bob' ? `
          <path d="M20 45 Q20 10 50 10 Q80 10 80 45 L82 70 Q82 80 70 80 L30 80 Q18 80 18 70 Z" fill="${hairColor}" />
        ` : hairStyle === 'ponytail' ? `
          <path d="M75 40 Q90 35 90 55 Q90 70 75 60 Z" fill="${hairColor}" />
        ` : hairStyle === 'long ponytail' ? `
          <path d="M75 40 Q95 35 95 70 Q95 100 75 90 Z" fill="${hairColor}" />
        ` : hairStyle === 'curly female' ? `
          <path d="M15 50 Q10 20 30 10 Q50 5 70 10 Q90 20 85 50 L90 85 Q90 95 70 95 L30 95 Q10 95 10 85 Z" fill="${hairColor}" />
        ` : ''}

        <!-- Body/Clothes -->
        ${clothing === 'shirt' ? `
          <path d="M20 100 Q20 70 50 70 Q80 70 80 100" fill="${clothingColor}" />
        ` : clothing === 'hoodie' ? `
          <path d="M20 100 Q20 65 50 65 Q80 65 80 100" fill="${clothingColor}" />
          <path d="M35 65 Q50 80 65 65" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="2" />
        ` : `
          <path d="M20 100 L35 70 L65 70 L80 100 Z" fill="${clothingColor}" />
        `}

        <!-- Neck -->
        <rect x="45" y="60" width="10" height="15" fill="${skinTone}" />

        <!-- Face -->
        <circle cx="50" cy="45" r="25" fill="${skinTone}" />

        <!-- Eyes -->
        ${eyeStyle === 'dot' ? `
          <circle cx="42" cy="42" r="2.5" fill="#333" />
          <circle cx="58" cy="42" r="2.5" fill="#333" />
        ` : eyeStyle === 'happy' ? `
          <path d="M38 42 Q42 38 46 42" fill="none" stroke="#333" stroke-width="2" />
          <path d="M54 42 Q58 38 62 42" fill="none" stroke="#333" stroke-width="2" />
        ` : eyeStyle === 'blink' ? `
          <circle cx="42" cy="42" r="2.5" fill="#333" />
          <line x1="54" y1="42" x2="62" y2="42" stroke="#333" stroke-width="2" />
        ` : `
          <rect x="34" y="36" width="15" height="11" rx="2" fill="#222" />
          <rect x="51" y="36" width="15" height="11" rx="2" fill="#222" />
          <line x1="48" y1="39" x2="52" y2="39" stroke="#222" stroke-width="2" />
          <path d="M36 38 L40 38 M53 38 L57 38" stroke="rgba(255,255,255,0.2)" stroke-width="1.5" stroke-linecap="round" />
        `}

        <!-- Mouth -->
        ${mouthStyle === 'smile' ? `
          <path d="M43 55 Q50 62 57 55" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round" />
        ` : mouthStyle === 'flat' ? `
          <line x1="44" y1="58" x2="56" y2="58" stroke="#333" stroke-width="2" stroke-linecap="round" />
        ` : `
          <circle cx="50" cy="58" r="3" fill="#333" />
        `}

        <!-- Hair Front (Top/Bangs) -->
        ${hairStyle === 'short' || hairStyle === 'ponytail' || hairStyle === 'long ponytail' ? `
          <path d="M25 40 Q25 15 50 15 Q75 15 75 40 Q50 32 25 40 Z" fill="${hairColor}" />
        ` : hairStyle === 'long' || hairStyle === 'bob' ? `
          <path d="M25 40 Q25 15 50 15 Q75 15 75 40 Q50 32 25 40 Z" fill="${hairColor}" />
          <path d="M25 35 Q35 25 50 25 Q65 25 75 35" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1" />
        ` : hairStyle === 'spiky' ? `
          <path d="M25 40 Q25 15 50 15 Q75 15 75 40 L75 35 L80 25 L70 30 L60 15 L50 25 L40 15 L30 30 L20 25 L25 35 Z" fill="${hairColor}" />
        ` : hairStyle === 'curly male' ? `
          <!-- Arched base for full coverage above eyes -->
          <path d="M22 40 Q22 10 50 10 Q78 10 78 40 Q50 30 22 40 Z" fill="${hairColor}" />
          <!-- Highly textured curly layer with more volume -->
          <path d="M22 40 
            C 12 35, 15 20, 25 15
            C 35 5, 45 5, 50 10
            C 55 5, 65 5, 75 15
            C 85 20, 88 35, 78 40
            Q 50 32 22 40 Z" fill="${hairColor}" />
          <!-- Individual curl definitions for depth -->
          <g fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" stroke-linecap="round">
            <path d="M30 22 Q35 17 40 22" />
            <path d="M60 22 Q65 17 70 22" />
            <path d="M42 15 Q50 10 58 15" />
            <path d="M28 32 Q35 27 42 32" />
            <path d="M58 32 Q65 27 72 32" />
          </g>
        ` : hairStyle === 'curly female' ? `
          <!-- Base cap arched for eyes -->
          <path d="M22 40 Q22 10 50 10 Q78 10 78 40 Q50 32 22 40 Z" fill="${hairColor}" />
          <!-- Cohesive curly texture front with proper framing -->
          <path d="M22 40
            C 12 45, 12 70, 12 85
            C 12 95, 25 95, 50 95
            C 75 95, 88 95, 88 85
            C 88 70, 88 45, 78 40
            Q 50 32 22 40 Z" fill="${hairColor}" />
          <!-- Top texture arcs -->
          <path d="M22 40 Q 18 30 25 20 Q 35 10 50 10 Q 65 10 75 20 Q 82 30 78 40 Q 50 32 22 40 Z" fill="${hairColor}" />
          <!-- Individual curls on the edges for volume -->
          <circle cx="15" cy="50" r="6" fill="${hairColor}" />
          <circle cx="85" cy="50" r="6" fill="${hairColor}" />
          <circle cx="12" cy="70" r="7" fill="${hairColor}" />
          <circle cx="88" cy="70" r="7" fill="${hairColor}" />
          <circle cx="35" cy="15" r="8" fill="${hairColor}" />
          <circle cx="65" cy="15" r="8" fill="${hairColor}" />
        ` : ''}

        <!-- Natural Hair Gloss (Always present) -->
        <g fill="none" stroke="white" stroke-width="1.2" opacity="0.12" stroke-linecap="round">
          <path d="M35 22 Q50 18 65 22" />
          ${hairStyle === 'curly male' ? `
            <path d="M42 14 Q50 10 58 14" />
          ` : ''}
          ${hairStyle === 'long' || hairStyle === 'bob' || hairStyle === 'curly female' ? `
            <path d="M22 60 L22 75 M78 60 L78 75" />
          ` : ''}
        </g>

        <!-- Hair Highlights (Enhanced Layered Strokes) -->
        ${highlightColor !== 'transparent' ? `
          <g fill="none" stroke-linecap="round">
            <!-- Glow Layer (Thicker, more transparent) -->
            <g stroke="${highlightColor}" stroke-width="3.2" opacity="0.35">
              ${renderHighlightPaths(hairStyle)}
            </g>
            <!-- Core Layer (Thinner, more solid) -->
            <g stroke="${highlightColor}" stroke-width="1.2" opacity="0.95">
              ${renderHighlightPaths(hairStyle)}
            </g>
          </g>
        ` : ''}

        ${(hairStyle === 'ponytail' || hairStyle === 'long ponytail') ? `
          <rect x="73" y="38" width="4" height="6" fill="rgba(0,0,0,0.2)" />
        ` : ''}
      </svg>
    `;
    return isDataUrl ? `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}` : svg.trim();
  };

  const handleSaveAvatar = () => {
    if (!token) return;
    setSaving(true);
    const avatarDataUrl = generateSVG(true);

    fetch(`${API_URL}/api/auth/avatar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ avatar: avatarDataUrl })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          // Update local user state
          if (user) {
            const updatedUser = { ...user, avatar: avatarDataUrl };
            login({ token, user: updatedUser });
          }
          alert('Avatar saved successfully!');
          navigate('/dashboard');
        } else {
          alert('Failed to save avatar: ' + data.error);
        }
      })
      .catch(err => alert('Error saving avatar: ' + err.message))
      .finally(() => setSaving(false));
  };

  return (
    <div className="home-container" style={{ padding: '40px 20px' }}>
      <header className="hero" style={{ marginBottom: '40px' }}>
        <h1>Avatar Creator</h1>
        <p>Design your personal 2D character!</p>
      </header>

      <div style={{ 
        display: 'flex', 
        flexDirection: window.innerWidth < 900 ? 'column' : 'row',
        gap: '40px',
        maxWidth: '1100px',
        margin: '0 auto',
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '30px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        alignItems: 'flex-start'
      }}>
        
        {/* PREVIEW */}
        <div style={{ 
          flex: '0 0 350px', 
          width: '100%',
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f8f9fa', 
          padding: '40px', 
          borderRadius: '25px',
          border: '2px solid #eee',
          position: 'sticky',
          top: '20px'
        }}>
          <div style={{ 
            width: '250px', 
            height: '250px', 
            borderRadius: '50%', 
            overflow: 'hidden',
            boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
            backgroundColor: 'white',
            marginBottom: '30px'
          }}>
            <img 
              src={generateSVG(true)} 
              alt="Avatar Preview" 
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <button 
            onClick={handleSaveAvatar}
            className="view-btn" 
            disabled={saving}
            style={{ width: '100%', padding: '15px', fontSize: '1.1rem' }}
          >
            {saving ? 'Saving...' : 'Set as Profile Picture'}
          </button>
          <button 
            onClick={() => navigate('/dashboard')}
            className="secondary-btn" 
            style={{ width: '100%', marginTop: '15px', padding: '12px' }}
          >
            Cancel
          </button>
        </div>

        {/* CONTROLS */}
        <div style={{ flex: 1, width: '100%', textAlign: 'left' }}>
          
          <ControlSection label="Skin Tone">
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {skinTones.map(t => (
                <ColorOption key={t} color={t} active={skinTone === t} onClick={() => setSkinTone(t)} />
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Hair Style">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
              {hairStyles.map(s => (
                <button 
                  key={s} 
                  onClick={() => setHairStyle(s)}
                  style={{ ...buttonStyle, backgroundColor: hairStyle === s ? '#e3f2fd' : 'white', border: hairStyle === s ? '2px solid var(--primary-color)' : '1px solid #ddd' }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Hair Color">
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {hairColors.map(c => (
                <ColorOption key={c} color={c} active={hairColor === c} onClick={() => setHairColor(c)} />
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Hair Highlights">
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {highlightColors.map(c => (
                <div 
                  key={c}
                  onClick={() => setHighlightColor(c)}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: c === 'transparent' ? '#fff' : c, 
                    backgroundImage: c === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)' : 'none',
                    backgroundSize: c === 'transparent' ? '10px 10px' : 'auto',
                    backgroundPosition: c === 'transparent' ? '0 0, 5px 5px' : 'auto',
                    cursor: 'pointer',
                    border: highlightColor === c ? '3px solid #333' : '2px solid transparent',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title={c === 'transparent' ? 'None' : c}
                >
                  {c === 'transparent' && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(45deg)', width: '100%', height: '2px', backgroundColor: '#e74c3c' }} />
                  )}
                </div>
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Eye Style">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
              {eyeStyles.map(s => (
                <button 
                  key={s} 
                  onClick={() => setEyeStyle(s)}
                  style={{ ...buttonStyle, backgroundColor: eyeStyle === s ? '#e3f2fd' : 'white', border: eyeStyle === s ? '2px solid var(--primary-color)' : '1px solid #ddd' }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Expression">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
              {mouthStyles.map(s => (
                <button 
                  key={s} 
                  onClick={() => setMouthStyle(s)}
                  style={{ ...buttonStyle, backgroundColor: mouthStyle === s ? '#e3f2fd' : 'white', border: mouthStyle === s ? '2px solid var(--primary-color)' : '1px solid #ddd' }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Clothing Type">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
              {clothingTypes.map(t => (
                <button 
                  key={t} 
                  onClick={() => setClothing(t)}
                  style={{ ...buttonStyle, backgroundColor: clothing === t ? '#e3f2fd' : 'white', border: clothing === t ? '2px solid var(--primary-color)' : '1px solid #ddd' }}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Clothing Color">
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {clothingColors.map(c => (
                <ColorOption key={c} color={c} active={clothingColor === c} onClick={() => setClothingColor(c)} />
              ))}
            </div>
          </ControlSection>

          <ControlSection label="Background Color">
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {backgroundColors.map(c => (
                <ColorOption key={c} color={c} active={background === c} onClick={() => setBackground(c)} />
              ))}
            </div>
          </ControlSection>

        </div>
      </div>
    </div>
  );
};

const ControlSection: React.FC<{ label: string, children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: '30px' }}>
    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '15px', fontSize: '1.1rem', color: '#2c3e50' }}>{label}</label>
    {children}
  </div>
);

const ColorOption: React.FC<{ color: string, active: boolean, onClick: () => void }> = ({ color, active, onClick }) => (
  <div 
    onClick={onClick}
    style={{ 
      width: '40px', 
      height: '40px', 
      borderRadius: '50%', 
      backgroundColor: color, 
      cursor: 'pointer',
      border: active ? '3px solid #333' : '2px solid transparent',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s'
    }}
    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
  />
);

const buttonStyle: React.CSSProperties = {
  padding: '12px',
  borderRadius: '12px',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'all 0.2s',
  fontSize: '0.9rem'
};

export default AvatarCreator;
